import { useEffect, useMemo, useState } from 'react';
import styles from './AppLoadingScreen.module.css';

const MESSAGE_SETS = {
  auth: ['Starting up…', 'Checking your session…', 'Almost ready…'],
  workspace: ['Loading your data…', 'Syncing…', 'Preparing your workspace…'],
  route: ['Loading view…'],
} as const;

export type AppLoadingVariant = keyof typeof MESSAGE_SETS;

type Props = {
  variant?: AppLoadingVariant;
  messages?: readonly string[];
};

export function AppLoadingScreen({ variant = 'auth', messages }: Props) {
  const pool = useMemo(() => messages ?? MESSAGE_SETS[variant], [messages, variant]);
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    setIndex(0);
    setVisible(true);
  }, [pool]);

  useEffect(() => {
    if (pool.length <= 1) return undefined;

    const interval = window.setInterval(() => {
      setVisible(false);
      window.setTimeout(() => {
        setIndex((current) => (current + 1) % pool.length);
        setVisible(true);
      }, 320);
    }, 2800);

    return () => window.clearInterval(interval);
  }, [pool]);

  const message = pool[index] ?? pool[0] ?? 'Loading…';

  return (
    <div className={styles.screen} role="status" aria-live="polite" aria-busy="true">
      <div className={styles.content}>
        <div className={styles.mark}>
          <div className={styles.ring} aria-hidden="true">
            <span className={styles.ringTrack} />
            <span className={styles.ringArc} />
          </div>
          <img
            className={styles.logo}
            src="/assets/logos/crafting-icon.svg"
            alt=""
            width={36}
            height={36}
          />
        </div>
        <div className={styles.brand}>
          <span className={styles.brandTitle}>Craftin</span>
          <span className={styles.brandSub}>Platform</span>
        </div>
        <div className={styles.messageWrap}>
          <p
            className={`${styles.message} ${visible ? styles.messageVisible : styles.messageHidden}`}
          >
            {message}
          </p>
        </div>
        <div className={styles.progress} aria-hidden="true">
          <span className={styles.progressBar} />
        </div>
      </div>
    </div>
  );
}
